import React from 'react';
import PropTypes from 'prop-types';
import Icons from '../Icons';
import { CustomTooltip } from '../Poppers';
import { DomUtils } from '../../utils/dom-utils';
import { Themes } from '../../style/theme-consts';
import * as S from './styled';

/**
 * Displays a clickable file download row with an icon, truncated file name, and extension.
 * Shows a small loading indicator on the top-right corner of the download icon while `isLoading` is true.
 *
 * @param {string} fileName - The file name to display (without extension)
 * @param {string} fileExt - The file extension to display (e.g. '.pdf')
 * @param {boolean} isLoading - When true, shows a loading overlay on the icon and disables interaction
 * @param {function} onClick - Click handler
 * @param {number} [maxFileNameWidth] - Max width of the file name in pixels (default: 200)
 * @param {string} [testId] - data-testid attribute
 */
const FileDownloadLink = ({ fileName, fileExt, isLoading, onClick, maxFileNameWidth, testId }) => {
  const { theme, elementRef } = DomUtils.useDetectParentTheme();

  return (
    <S.Wrapper ref={elementRef} $disabled={isLoading} onClick={onClick} data-testid={testId}>
      <S.IconWrapper>
        <Icons.Download size={16} />
        {isLoading && <S.LoadingIcon size={10} rotate color="var(--button-loading-icon-color)" />}
      </S.IconWrapper>
      <CustomTooltip title={fileName} mode={theme || Themes.LIGHT}>
        <span>
          <S.FileName $maxWidth={maxFileNameWidth}>{fileName}</S.FileName>
        </span>
      </CustomTooltip>
      {fileExt && <S.FileExt>{fileExt}</S.FileExt>}
    </S.Wrapper>
  );
};

FileDownloadLink.propTypes = {
  fileName: PropTypes.string.isRequired,
  fileExt: PropTypes.string,
  isLoading: PropTypes.bool,
  onClick: PropTypes.func,
  maxFileNameWidth: PropTypes.number,
  testId: PropTypes.string
};

export default FileDownloadLink;
